
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getCurrentUser } from "../utils/auth";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";
const RESTORE_SECRET = process.env.REACT_APP_ROUTING_STATUS_RESTORE_SECRET || "";

const SNAPSHOT_TYPES = {
  routing: {
    label: "Routing Status",
    description: "CSV snapshots generated by the routing status cron job.",
    listPath: "/internal/routing-status-snapshots",
    downloadPath: (filename) => `/internal/routing-status-snapshots/${encodeURIComponent(filename)}`,
    restorePath: "/internal/routing-status-restore",
    tableName: "routing_status",
    warning: (filename) =>
      `This will replace the current routing_status table with data from ${filename}. This action cannot be undone.`,
  },
  po: {
    label: "PO Details",
    description: "Snapshot CSV exports for the po_details dataset behind the PO Details page.",
    listPath: "/internal/po-details-snapshots",
    downloadPath: (filename) => `/internal/po-details-snapshots/${encodeURIComponent(filename)}`,
    restorePath: "/internal/po-details-restore",
    tableName: "po_details",
    warning: (filename) =>
      `This will replace the current po_details table with data from ${filename}. This action cannot be undone.`,
  },
  mrp: {
    label: "MRP Inventory",
    description: "Snapshots for the mrp_inventory_snapshot_rows dataset (daily MRP Inventory persistence).",
    listPath: "/api/mrp/inventory/snapshots",
    snapshotDownloadPath: (snapshotDate) => `/api/mrp/inventory/snapshot?date=${encodeURIComponent(snapshotDate)}`,
    snapshotDownloadFile: (snapshotDate) => `mrp_inventory_snapshot_${snapshotDate}.json`,
  },
  intransit: {
    label: "In Transit",
    description: "Snapshots for In Transit - Arrival rows used by Walmart Ladder Supplier Receipts.",
    listPath: "/api/mrp/intransit-arrival/snapshots",
    snapshotDownloadPath: (snapshotDate) =>
      `/api/mrp/intransit-arrival/snapshot?date=${encodeURIComponent(snapshotDate)}`,
    snapshotDownloadFile: (snapshotDate) => `mrp_intransit_arrival_snapshot_${snapshotDate}.json`,
  },
};

const TAB_KEYS = ["routing", "po", "mrp", "intransit"];

function formatFileSize(bytes) {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value < 0) return "-";
  if (value === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const idx = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  const result = value / Math.pow(1024, idx);
  const display = result >= 100 ? result.toFixed(0) : result >= 10 ? result.toFixed(1) : result.toFixed(2);
  return `${display} ${units[idx]}`;
}

function formatUtcTimestamp(value) {
  if (!value) return "-";
  const iso = /\d{4}-\d{2}-\d{2}T/.test(value) ? value : `${value}Z`;
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return value;
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())} ${pad(dt.getUTCHours())}:${pad(
    dt.getUTCMinutes()
  )}:${pad(dt.getUTCSeconds())} UTC`;
}

export default function Snapshots() {
  const me = getCurrentUser();
  const [activeType, setActiveType] = useState("routing");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [downloadError, setDownloadError] = useState("");
  const [downloading, setDownloading] = useState("");
  const [restoreTarget, setRestoreTarget] = useState("");
  const [restoring, setRestoring] = useState(false);
  const [restoreMessage, setRestoreMessage] = useState("");
  const [restoreGlobalError, setRestoreGlobalError] = useState("");
  const [restoreModalError, setRestoreModalError] = useState("");

  const headers = useMemo(() => {
    const hdrs = { "Content-Type": "application/json" };
    if (me?.email) hdrs["X-User-Email"] = me.email;
    if (me?.role) hdrs["X-User-Role"] = me.role;
    return hdrs;
  }, [me?.email, me?.role]);

  const loadSnapshots = useCallback(async () => {
    const config = SNAPSHOT_TYPES[activeType];
    setLoading(true);
    setError("");
    try {
      const resp = await fetch(`${API_BASE}${config.listPath}`, { headers });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.error || "Unable to load snapshots.");
      }
      if (activeType === "mrp" || activeType === "intransit") {
        setFiles(Array.isArray(data?.snapshots) ? data.snapshots : []);
      } else {
        setFiles(Array.isArray(data?.files) ? data.files : []);
      }
    } catch (err) {
      console.error(err);
      setError(err.message || "Unable to load snapshots.");
    } finally {
      setLoading(false);
    }
  }, [activeType, headers]);

  useEffect(() => {
    loadSnapshots();
  }, [loadSnapshots]);

  const handleTabChange = useCallback(
    (type) => {
      if (type === activeType) return;
      setActiveType(type);
      setFiles([]);
      setError("");
      setDownloadError("");
      setRestoreMessage("");
      setRestoreGlobalError("");
      setRestoreModalError("");
      setRestoreTarget("");
    },
    [activeType]
  );

  const handleDownload = useCallback(
    async (filename) => {
      if (!filename) return;
      if (activeType === "mrp" || activeType === "intransit") return;
      const config = SNAPSHOT_TYPES[activeType];
      setDownloading(filename);
      setDownloadError("");
      try {
        const downloadHeaders = { ...headers };
        delete downloadHeaders["Content-Type"];
        const resp = await fetch(`${API_BASE}${config.downloadPath(filename)}`, { headers: downloadHeaders });
        if (!resp.ok) {
          let message = "Download failed.";
          try {
            const payload = await resp.json();
            if (payload?.error) message = payload.error;
          } catch (e) {
            // ignore
          }
          throw new Error(message);
        }
        const blob = await resp.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => window.URL.revokeObjectURL(url), 1500);
      } catch (err) {
        console.error(err);
        setDownloadError(err.message || "Unable to download snapshot.");
      } finally {
        setDownloading("");
      }
    },
    [activeType, headers]
  );

  const handleSnapshotJsonDownload = useCallback(
    async (snapshotDate) => {
      if (!snapshotDate) return;
      const config = SNAPSHOT_TYPES[activeType];
      if (!config?.snapshotDownloadPath) return;
      setDownloading(snapshotDate);
      setDownloadError("");
      try {
        const resp = await fetch(`${API_BASE}${config.snapshotDownloadPath(snapshotDate)}`, { headers });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(data?.error || "Download failed.");
        }
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = config.snapshotDownloadFile
          ? config.snapshotDownloadFile(snapshotDate)
          : `snapshot_${snapshotDate}.json`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => window.URL.revokeObjectURL(url), 1500);
      } catch (err) {
        console.error(err);
        setDownloadError(err.message || "Unable to download snapshot.");
      } finally {
        setDownloading("");
      }
    },
    [activeType, headers]
  );

  const openRestoreModal = useCallback((filename) => {
    setRestoreModalError("");
    setRestoreGlobalError("");
    setRestoreMessage("");
    setRestoreTarget(filename);
  }, []);

  const closeRestoreModal = useCallback(() => {
    if (restoring) return;
    setRestoreModalError("");
    setRestoreTarget("");
  }, [restoring]);

  const handleConfirmRestore = useCallback(async () => {
    if (!restoreTarget) return;
    const config = SNAPSHOT_TYPES[activeType];
    setRestoring(true);
    setRestoreModalError("");
    setRestoreGlobalError("");
    try {
      const restoreHeaders = { ...headers, "Content-Type": "application/json" };
      if (RESTORE_SECRET) {
        restoreHeaders["X-RESTORE-SECRET"] = RESTORE_SECRET;
      }
      const resp = await fetch(`${API_BASE}${config.restorePath}`, {
        method: "POST",
        headers: restoreHeaders,
        body: JSON.stringify({ filename: restoreTarget }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data.status !== "ok") {
        throw new Error(data.message || data.error || "Restore failed.");
      }
      setRestoreMessage(
        `Restore completed (${config.tableName}, backend ${data.backend || "unknown"}). Rows restored: ${
          data.rows_restored ?? 0
        }.`
      );
      setRestoreTarget("");
      await loadSnapshots();
    } catch (err) {
      console.error(err);
      const message = err.message || "Unable to restore snapshot.";
      setRestoreModalError(message);
      setRestoreGlobalError(message);
    } finally {
      setRestoring(false);
    }
  }, [activeType, headers, loadSnapshots, restoreTarget]);

  const activeConfig = SNAPSHOT_TYPES[activeType];
  const rows = Array.isArray(files) ? files : [];
  const isSnapshotJsonTab = activeType === "mrp" || activeType === "intransit";

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold">Snapshots</h1>
          <p className="text-sm text-slate-600">
            Browse and restore CSV backups for Routing Status / PO Details, plus JSON snapshots for MRP and In Transit.
          </p>
        </div>
        <button
          type="button"
          onClick={loadSnapshots}
          disabled={loading}
          className="rounded-md border px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
        >
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {TAB_KEYS.map((key) => {
          const cfg = SNAPSHOT_TYPES[key];
          const isActive = key === activeType;
          return (
            <button
              key={key}
              type="button"
              onClick={() => handleTabChange(key)}
              className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
                isActive ? "border-blue-500 bg-blue-50 text-blue-700" : "border-transparent bg-slate-100 text-slate-700"
              }`}
            >
              {cfg.label}
            </button>
          );
        })}
      </div>

      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
        {activeConfig.description}
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 flex items-start justify-between gap-3">
          <span>{error}</span>
          <button type="button" onClick={loadSnapshots} className="text-red-700 underline decoration-dotted">
            Retry
          </button>
        </div>
      )}

      {downloadError && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 flex items-start justify-between gap-3">
          <span>{downloadError}</span>
          <button
            type="button"
            onClick={() => setDownloadError("")}
            className="text-amber-800 underline decoration-dotted"
          >
            Dismiss
          </button>
        </div>
      )}

      {restoreMessage && (
        <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800 flex items-start justify-between gap-3">
          <span>{restoreMessage}</span>
          <button
            type="button"
            onClick={() => setRestoreMessage("")}
            className="text-green-800 underline decoration-dotted"
          >
            Dismiss
          </button>
        </div>
      )}

      {restoreGlobalError && !restoreTarget && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 flex items-start justify-between gap-3">
          <span>{restoreGlobalError}</span>
          <button
            type="button"
            onClick={() => setRestoreGlobalError("")}
            className="text-red-700 underline decoration-dotted"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="overflow-auto border rounded-md">
        <table className="min-w-full border-collapse text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              {isSnapshotJsonTab ? (
                <>
                  <th className="px-3 py-2 text-left font-semibold">Snapshot Date</th>
                  <th className="px-3 py-2 text-left font-semibold">Generated At (UTC)</th>
                  <th className="px-3 py-2 text-left font-semibold">Rows</th>
                  <th className="px-3 py-2 text-left font-semibold">Status</th>
                  <th className="px-3 py-2 text-left font-semibold">Actions</th>
                </>
              ) : (
                <>
                  <th className="px-3 py-2 text-left font-semibold">Filename</th>
                  <th className="px-3 py-2 text-left font-semibold">Size</th>
                  <th className="px-3 py-2 text-left font-semibold">Last Modified (UTC)</th>
                  <th className="px-3 py-2 text-left font-semibold">Actions</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={isSnapshotJsonTab ? 5 : 4} className="px-3 py-6 text-center text-slate-500">
                  Loading snapshots...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={isSnapshotJsonTab ? 5 : 4} className="px-3 py-6 text-center text-slate-500">
                  No snapshots found.
                </td>
              </tr>
            ) : isSnapshotJsonTab ? (
              rows.map((snapshot) => (
                <tr key={`${snapshot.snapshot_date}-${snapshot.generated_at}`}>
                  <td className="border-t px-3 py-2 text-slate-800">{snapshot.snapshot_date || "-"}</td>
                  <td className="border-t px-3 py-2 text-slate-700">{formatUtcTimestamp(snapshot.generated_at)}</td>
                  <td className="border-t px-3 py-2 text-slate-700">{snapshot.rows ?? "-"}</td>
                  <td className="border-t px-3 py-2 text-slate-700">{snapshot.status || "-"}</td>
                  <td className="border-t px-3 py-2">
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => handleSnapshotJsonDownload(snapshot.snapshot_date)}
                        className="rounded-md border px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-60"
                        disabled={downloading === snapshot.snapshot_date}
                      >
                        {downloading === snapshot.snapshot_date ? "Downloading..." : "Download JSON"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              rows.map((file) => (
                <tr key={file.filename}>
                  <td className="border-t px-3 py-2 text-slate-800">{file.filename}</td>
                  <td className="border-t px-3 py-2 text-slate-700">{formatFileSize(file.size_bytes)}</td>
                  <td className="border-t px-3 py-2 text-slate-700">{formatUtcTimestamp(file.modified_utc)}</td>
                  <td className="border-t px-3 py-2">
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => handleDownload(file.filename)}
                        className="rounded-md border px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-60"
                        disabled={downloading === file.filename}
                      >
                        {downloading === file.filename ? "Downloading..." : "Download"}
                      </button>
                      <button
                        type="button"
                        onClick={() => openRestoreModal(file.filename)}
                        className="rounded-md border border-red-200 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-60"
                        disabled={restoring}
                      >
                        Restore
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {restoreTarget && !isSnapshotJsonTab && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md space-y-4 rounded-lg bg-white p-5 shadow-xl">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Confirm Restore</h2>
              <p className="mt-2 text-sm text-slate-700">
                {activeConfig.warning(restoreTarget)}
              </p>
            </div>
            {restoreModalError && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {restoreModalError}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={closeRestoreModal}
                disabled={restoring}
                className="rounded-md border px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmRestore}
                disabled={restoring}
                className="rounded-md border border-red-300 bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-500 disabled:opacity-70"
              >
                {restoring ? "Restoring..." : "Confirm Restore"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
